/**
 * T060/T090: Prompts List Page
 * Main page for viewing and browsing prompts with filter support
 */

import { Link, useNavigate } from '@tanstack/react-router'
import { Plus, FileText } from 'lucide-react'
import { usePrompts } from '@/hooks/usePrompts'
import { Button } from '@/components/ui/button'
import { PromptList } from '@/components/prompts/prompt-list'
import { Pagination } from '@/components/common/pagination'
import { EmptyState } from '@/components/common/empty-state'
import { ErrorMessage } from '@/components/common/error-message'
import { PromptFilters, type PromptFiltersState } from '@/components/prompts/prompt-filters'
import { PromptListSkeleton } from '@/components/prompts/prompt-card-skeleton'
import { Route } from '@/routes/_authenticated/prompts/index'

export function PromptsListPage() {
  const navigate = useNavigate({ from: Route.fullPath })
  const search = Route.useSearch()
  const limit = 12

  // Convert URL search params to filter state
  const filters: PromptFiltersState = {
    q: search.q,
    categoryId: search.categoryId,
    tagIds: search.tagIds ? search.tagIds.split(',').filter(Boolean) : [],
  }

  // Update URL when filters change
  const handleFiltersChange = (newFilters: PromptFiltersState) => {
    navigate({
      search: {
        q: newFilters.q || undefined,
        categoryId: newFilters.categoryId || undefined,
        tagIds: newFilters.tagIds.length > 0 ? newFilters.tagIds.join(',') : undefined,
        page: 1, // Reset to page 1 when filters change
      },
    })
  }

  // Handle page change
  const handlePageChange = (newPage: number) => {
    navigate({
      search: {
        ...search,
        page: newPage,
      },
    })
  }

  const { data, isLoading, error, refetch } = usePrompts({
    page: search.page,
    limit,
    q: filters.q,
    categoryId: filters.categoryId,
    tagIds: filters.tagIds.length > 0 ? filters.tagIds : undefined,
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Prompts</h1>
          <Button asChild>
            <Link to="/prompts/new">
              <Plus className="mr-2 h-4 w-4" />
              New Prompt
            </Link>
          </Button>
        </div>
        <PromptFilters filters={filters} onChange={handleFiltersChange} />
        <PromptListSkeleton count={limit} />
      </div>
    )
  }

  if (error) {
    return (
      <ErrorMessage
        title="Failed to load prompts"
        message={error.message}
        onRetry={refetch}
      />
    )
  }

  const prompts = data?.data ?? []
  const pagination = data?.pagination

  const hasActiveFilters = filters.q || filters.categoryId || filters.tagIds.length > 0

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Prompts</h1>
        <Button asChild>
          <Link to="/prompts/new">
            <Plus className="mr-2 h-4 w-4" />
            New Prompt
          </Link>
        </Button>
      </div>

      <PromptFilters filters={filters} onChange={handleFiltersChange} />

      {prompts.length === 0 ? (
        <EmptyState
          icon={FileText}
          title={hasActiveFilters ? 'No prompts found' : 'No prompts yet'}
          description={
            hasActiveFilters
              ? 'Try adjusting your filters or search terms'
              : 'Create your first prompt to get started'
          }
          action={
            hasActiveFilters ? (
              <Button
                variant="outline"
                onClick={() => handleFiltersChange({ tagIds: [] })}
              >
                Clear Filters
              </Button>
            ) : (
              <Button asChild>
                <Link to="/prompts/new">
                  <Plus className="mr-2 h-4 w-4" />
                  Create Prompt
                </Link>
              </Button>
            )
          }
        />
      ) : (
        <>
          <PromptList prompts={prompts} />
          {pagination && (
            <div className="mt-8">
              <Pagination
                page={pagination.page}
                totalPages={pagination.total_pages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}
